import { useEffect, useRef, useState } from 'react'

import { useFloorPlan } from '../hooks/useFloorPlan'
import { useSelector } from 'react-redux'

import { Box, Flex } from '@chakra-ui/react'

import { DeviceType } from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { selectEditMode, selectFacilityDrawerOpen } from '@/redux/ui/uiSlice'
import { FacilityDrawer } from '@/redux/ui/uiSlice.types'
import { mixpanel } from '@/utils/analytics'

import { useFloorChart, useFloorPosition } from '../hooks'
import { ConnectionI, DeviceI, Size } from '../types/types'
import { convertCoordinatesDOMtoSVG } from '../utils/chart'
import {
  mapCheckpointData,
  mapConnectionsData,
  mapDevicesData,
} from '../utils/floorMappedData'
import { DeviceIconSizeSlider } from './DeviceIconSizeSlider'

interface FloorChartContainerIProps {
  showBinIcon: boolean
  chartSize: number[] | Size
  chartInsets: {
    left: number
    top: number
    right: number
    bottom: number
  }
  imageUrl: string
  imageSize: Size
  devices: DeviceI[]
  connections: any[]
  isGuardTour?: boolean
}

// Values found through trial & error and looking at the UI and seeing what felt reasonable
const MIN_FLOOR_PLAN_ICON_SIZE = 7.5
const MAX_FLOOR_PLAN_ICON_SIZE = 75

export const FloorChartContainer = ({
  showBinIcon,
  chartSize,
  chartInsets,
  imageUrl,
  imageSize,
  devices,
  connections,
  isGuardTour,
}: FloorChartContainerIProps) => {
  const me = useSelector(selectMe)
  const svgRef = useRef(null)
  const mapImageRef = useRef(null)
  const mapGroupRef = useRef(null)

  const [mappedDevices, setMappedDevices] = useState<DeviceI[]>(null)
  const [mappedCheckpointDevices, setMappedCheckpointDevices] =
    useState<DeviceI[]>(null)
  const [mappedConnections, setMappedConnections] =
    useState<ConnectionI[]>(null)
  const [isRendered, setRendered] = useState(false)
  const [isZoomed, setZoomed] = useState(false)

  const drawerOpen: FacilityDrawer = useSelector(selectFacilityDrawerOpen)

  const shouldShowIconResizer = !(
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'
  )

  const { validateDevicePosition, validateMapPosition, binPosition } =
    useFloorPosition(chartSize as Size, chartInsets, imageSize)

  const [chartWidth, chartHeight] = chartSize
  const isEditMode = useSelector(selectEditMode)
  const {
    floorId,
    checkpointDevices,
    highlightedDeviceIds,
    floorPlanIconSize,
    onIconResize,
    onFloorPlanContainerClick,
    onDeviceClicked,
    onDeviceDragged,
    onDeviceDragEnd,
    onDeviceHover,
    onDeviceHoverOut,
    onMoveCheckPointClicked,
    onConnectionButtonClicked,
    onRemoveCheckpoint,
    handleDeviceDropped,
  } = useFloorPlan()

  const onDrop = (e) => {
    e.stopPropagation()
    const { x, y } = convertCoordinatesDOMtoSVG(
      svgRef.current,
      mapGroupRef.current,
      e.clientX,
      e.clientY
    )
    const isValid = validateMapPosition(x, y)
    handleDeviceDropped([x, y], isValid)
  }

  const refreshDevices = () => {
    const devicesData = mapDevicesData(devices)
    const connectionsData = mapConnectionsData(connections)
    const checkpointData = mapCheckpointData(checkpointDevices, floorId)
    setMappedDevices(devicesData)
    setMappedConnections(connectionsData)
    setMappedCheckpointDevices(checkpointData)
  }

  const { render, centerZoom } = useFloorChart({
    floorPlanIconSize: floorPlanIconSize,
    devices: mappedDevices,
    checkpoints: mappedCheckpointDevices,
    connections: mappedConnections,
    interactionsEnabled: true,
    onDeviceUpdated: (d: DeviceI) =>
      onDeviceDragged(d, validateDevicePosition(d)),
    onDeviceReleased: (d: DeviceI) => {
      onDeviceDragEnd(d, validateDevicePosition(d))
      if (d.deviceType === DeviceType.Checkpoint) {
        mixpanel.track('Button Press: Map Checkpoint - End Moving', {
          checkpoint_id: d.checkpoint.id,
          facility_id: d.checkpoint.facility.id,
          floor_id: d.checkpoint.floor.id,
          role: me.roleName,
        })
      }
    },
    onDeviceClicked: onDeviceClicked,
    onDeviceHover: onDeviceHover,
    onDeviceHoverOut: onDeviceHoverOut,
    onConnectionButtonClicked: onConnectionButtonClicked,
    onMoveCheckpoint: (d: DeviceI) => {
      onMoveCheckPointClicked(d.id)
      if (!d.editCheckpointPos) {
        mixpanel.track('Button Press: Map Checkpoint - Move Placement', {
          checkpoint_id: d.checkpoint.id,
          facility_id: d.checkpoint.facility.id,
          floor_id: d.checkpoint.floor.id,
          role: me.roleName,
        })
      }
    },
    onRemoveCheckpoint: onRemoveCheckpoint,
    highlightedDeviceIds: highlightedDeviceIds,
  })

  useEffect(() => {
    if (svgRef.current && !isRendered) {
      render(svgRef.current, mapImageRef.current)
      setRendered(true)
    }
  }, [svgRef])

  useEffect(() => {
    if (isRendered && imageSize) {
      centerZoom(svgRef.current, mapImageRef.current)
      setZoomed(true)
      refreshDevices()
    }
  }, [svgRef, imageSize, isRendered])

  useEffect(() => {
    if (!isRendered || !devices || !isZoomed) return
    refreshDevices()
  }, [devices, connections, imageSize, isRendered, isZoomed])

  return (
    <Box width={chartWidth}>
      <Flex
        onClick={onFloorPlanContainerClick}
        onDragOver={(e) => {
          e.preventDefault()
        }}
        onDrop={onDrop}
        width='100%'
      >
        <svg height={chartHeight} ref={svgRef} width={chartWidth}>
          <defs>
            <linearGradient id='device-shape-gradient' x2='0.35' y2='1'>
              <stop offset='0%' stopColor='var(--color-stop)' />
              <stop offset='30%' stopColor='var(--color-stop)' />
              <stop offset='100%' stopColor='var(--color-bot)' />
            </linearGradient>
          </defs>
          <g className='map-group' ref={mapGroupRef}>
            <image
              className='map'
              height={imageSize ? imageSize[1] : 0}
              href={imageUrl}
              ref={mapImageRef}
              width={imageSize ? imageSize[0] : 0}
              x={0}
              y={0}
            />
            {!isGuardTour ? (
              <>
                <g className='camera-tools-group'></g>
                <g className={`connections-group ${isEditMode && 'editOn'}`} />
                <g className={`camera-group ${isEditMode && 'editOn'}`} />
                <g className={`door-group ${isEditMode && 'editOn'}`} />
                <g className={`alarm-group ${isEditMode && 'editOn'}`} />
              </>
            ) : (
              <g className='checkpoint-group' />
            )}
          </g>
          {showBinIcon && (
            <image
              height={binPosition.height}
              href='FloorTrash.png'
              width={binPosition.width}
              x={binPosition.x}
              y={binPosition.y}
            />
          )}
        </svg>
      </Flex>

      {shouldShowIconResizer && (
        <Box bottom='5' left='5' position='absolute' zIndex={10}>
          <DeviceIconSizeSlider
            max={MAX_FLOOR_PLAN_ICON_SIZE}
            min={MIN_FLOOR_PLAN_ICON_SIZE}
            onChange={onIconResize}
            value={floorPlanIconSize}
          />
        </Box>
      )}
    </Box>
  )
}
